import { useMemo } from 'react';

import { useDTicketSubscriptionCancellationData } from '../../dticketSubscriptionCancel/hooks';
import { getEditCaseConfig } from '../helpers/getEditCaseConfig';
import {
	IActionConfig,
	IEditConfig,
	INavigationHandlers,
	TActionType,
} from '../types';

interface IEditConfigResult {
	isLoading: boolean;
	config: IEditConfig | null;
}

export const useDTicketSubscriptionEditConfig = (
	handlers: INavigationHandlers
): IEditConfigResult => {
	const {
		navigateToSubscriptionPause,
		navigateToSubscriptionCancel,
		onRevoke,
	} = handlers;

	const {
		isLoading,
		cancellationCase,
		dates,
		isPauseEndingSoon,
		hasPause,
		isInvalid,
		hasPendingCancel,
		disallowCancelCancellation,
	} = useDTicketSubscriptionCancellationData();

	const config = useMemo(() => {
		if (!cancellationCase || !dates) return null;

		// The backend forbids revoking the cancellation (e.g. the subscription was
		// cancelled by an admin, or payment has failed) — none of the actions offered here (pause, cancel,
		// revoke) are available, so redirect away.
		// TODO: clarify all possible cases with BE, maybe isInvalid can be dropped later (MC-11534)
		if ((isInvalid && hasPendingCancel) || disallowCancelCancellation) {
			return null;
		}

		const actionHandlers: Record<TActionType, () => void> = {
			pause: navigateToSubscriptionPause,
			cancel: navigateToSubscriptionCancel,
			revoke: onRevoke,
		};

		const caseConfig = getEditCaseConfig(cancellationCase, {
			isPauseEndingSoon,
			hasPause,
		});

		const firstAction: IActionConfig = {
			...caseConfig.firstAction,
			onClick: actionHandlers[caseConfig.firstAction.type],
		};

		const secondAction: IActionConfig | null = caseConfig.secondAction
			? {
					...caseConfig.secondAction,
					onClick: actionHandlers[caseConfig.secondAction.type],
			  }
			: null;

		return {
			cancellationCase,
			firstAction,
			secondAction,
			dates,
			isInvalid,
			hasPendingCancel,
		};
	}, [
		cancellationCase,
		dates,
		isPauseEndingSoon,
		hasPause,
		isInvalid,
		hasPendingCancel,
		disallowCancelCancellation,
		navigateToSubscriptionPause,
		navigateToSubscriptionCancel,
		onRevoke,
	]);

	return { isLoading, config };
};
